import React, { useState, useEffect } from 'react';
import { Button, Tooltip, message } from 'antd';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import cn from 'classnames';
import {
  EmptyRow,
  TableRow,
  TableCell,
} from '../../../../../../components/customTableComponents';
import PhoneNumber from '../../../../../../components/PhoneNumber/PhoneNumber';
import CallDirection from '../../../CallDirection/CallDirection';
import { convertSeconds } from '../../../../../../helpers/convertSeconds';
import { declOfNum } from '../../../../../../helpers/dspx-utils';
import useCallRecord from '../../../../queries/useCallRecord';
import { getBodyCellContent, createDownloadLink } from '../../utils';
import { recordActionTypes } from '../../constants';
import {
  setCurrentCallInfo,
  setCurrentRecordUrl,
  setListenModalVisible,
} from '../../../../store/callHistoryActions';
import styles from './CallListRow.module.scss';

const requestsPerCell = 2;

const Content = ({ className, children }) => {
  return <p className={cn(styles.paragraph, className)}>{children}</p>;
};

const CallListRow = ({ call }) => {
  const dispatch = useDispatch();

  const [showAllRequests, setShowAllRequests] = useState(false);
  const [sendRecordRequest, setSendRecordRequest] = useState(false);
  const [recordActionType, setRecordActionType] = useState('');
  const [recordLink, setRecordLink] = useState(null);

  const requests = call.requests?.split(', ') || [];
  const expandableRequests = requests.length > requestsPerCell;

  const showAndHideRequestsBtnClick = expandableRequests
    ? () => {
        setShowAllRequests((prev) => !prev);
      }
    : undefined;

  const { data: callRecord, isFetching: isCallRecordFetching } = useCallRecord(
    call.uniqueId,
    {
      enabled: sendRecordRequest,
      staleTime: Infinity,
      select: (data) => {
        if (data && data !== 'Error') {
          const recordBuffer = Buffer.from(data, 'base64');
          const url = URL.createObjectURL(new Blob([recordBuffer]));
          return url;
        }

        return data;
      },
    }
  );

  const handleDownloadRecordBtnClick = () => {
    if (!sendRecordRequest) {
      setSendRecordRequest(true);
    }
    setRecordActionType(recordActionTypes.download);
  };

  const handlePlayRecordClick = () => {
    if (!sendRecordRequest) {
      setSendRecordRequest(true);
    }
    setRecordActionType(recordActionTypes.play);
  };

  useEffect(() => {
    if (callRecord && callRecord !== 'Error') {
      switch (recordActionType) {
        case recordActionTypes.download:
          recordLink
            ? recordLink.click()
            : setRecordLink(createDownloadLink(callRecord, call));
          setRecordActionType('');
          break;

        case recordActionTypes.play:
          dispatch(setListenModalVisible(true));
          dispatch(setCurrentRecordUrl(callRecord));
          dispatch(setCurrentCallInfo(call));
          setRecordActionType('');
          break;
      }
    } else {
      recordActionType && !isCallRecordFetching && message.info('Записи нет');
    }
  }, [callRecord, recordActionType, recordLink, call, isCallRecordFetching]);

  return (
    <>
      <EmptyRow />
      <TableRow>
        <TableCell
          className={cn(styles.cell, {
            [styles.cellContentTop]: showAllRequests,
          })}
          position="left"
        >
          <div className={styles.flexCol}>
            <Content className={styles.time}>
              {moment(call.createTime).format('DD.MM.YYYY')}
            </Content>
            <Content className={styles.time}>
              {moment(call.createTime).format('HH:mm')}
            </Content>
          </div>
        </TableCell>

        <TableCell
          className={cn(styles.cell, {
            [styles.cellContentTop]: showAllRequests,
          })}
        >
          <Content className={styles.highlighted}>
            {getBodyCellContent(call.serviceCompany)}
          </Content>
        </TableCell>

        <TableCell
          className={cn(styles.cell, {
            [styles.cellContentTop]: showAllRequests,
          })}
        >
          <div className={styles.flexRow}>
            <CallDirection direction={call.direction} />
            <PhoneNumber
              className={styles.paragraph}
              phoneNumber={call.callerId}
            />
          </div>
        </TableCell>

        <TableCell
          className={cn(styles.cell, {
            [styles.cellContentTop]: showAllRequests,
          })}
        >
          <Content>
            {getBodyCellContent(convertSeconds(call.waitingTime))}
          </Content>
        </TableCell>

        <TableCell
          className={cn(styles.cell, {
            [styles.cellContentTop]: showAllRequests,
          })}
        >
          <Content>
            {getBodyCellContent(convertSeconds(call.clearWaitingTime))}
          </Content>
        </TableCell>

        <TableCell
          className={cn(styles.cell, {
            [styles.cellContentTop]: showAllRequests,
          })}
        >
          <Content>{getBodyCellContent(convertSeconds(call.talkTime))}</Content>
        </TableCell>

        <TableCell
          className={cn(styles.cell, {
            [styles.cellContentTop]: showAllRequests,
          })}
        >
          {expandableRequests && (
            <div className={styles.flexCol}>
              <Content className={styles.requests}>
                {getBodyCellContent(
                  showAllRequests
                    ? call.requests
                    : requests.slice(0, requestsPerCell).join(', ')
                )}
              </Content>
              <Button
                type="link"
                className={styles.showHideRequestsBtn}
                onClick={showAndHideRequestsBtnClick}
              >
                {showAllRequests
                  ? 'Скрыть'
                  : `Показать еще ${
                      requests.length - requestsPerCell
                    } ${declOfNum(requests.length - requestsPerCell, [
                      'заявку',
                      'заявки',
                      'заявок',
                    ])}`}
              </Button>
            </div>
          )}

          {!expandableRequests && (
            <Content className={styles.requests}>
              {getBodyCellContent(call.requests)}
            </Content>
          )}
        </TableCell>

        <TableCell
          className={cn(styles.cell, {
            [styles.cellContentTop]: showAllRequests,
          })}
        >
          <Content>{getBodyCellContent(call.operator?.shortName)}</Content>
        </TableCell>

        <TableCell
          className={cn(styles.cell, {
            [styles.cellContentTop]: showAllRequests,
          })}
        >
          <Content
            className={cn({
              [styles.highlighted]: call.operator?.serviceCompany?.name,
            })}
          >
            {getBodyCellContent(call.operator?.serviceCompany?.name)}
          </Content>
        </TableCell>

        <TableCell
          className={cn(styles.cell, {
            [styles.cellContentTop]: showAllRequests,
          })}
          position="right"
        >
          <div className={styles.flexRow}>
            <Tooltip title="Прослушать">
              <Button
                shape="circle"
                className={styles.recordBtn}
                icon={
                  isCallRecordFetching &&
                  recordActionType === recordActionTypes.play
                    ? 'loading'
                    : 'play-circle'
                }
                onClick={handlePlayRecordClick}
              />
            </Tooltip>

            <Tooltip title="Скачать">
              <Button
                shape="circle"
                icon={
                  isCallRecordFetching &&
                  recordActionType === recordActionTypes.download
                    ? 'loading'
                    : 'download'
                }
                onClick={handleDownloadRecordBtnClick}
                className={styles.recordBtn}
              />
            </Tooltip>
          </div>
        </TableCell>
      </TableRow>
    </>
  );
};

export default CallListRow;
